/**
 * Cotton World mega menu:
 * - Sync header --submenu-height when layout changes.
 * - Hover / focus primary items to show or clear the middle column.
 * - Reset when the parent submenu opens.
 */
(function () {
  'use strict';

  function updateMegaMenuSubmenuMetrics(submenu) {
    if (!(submenu instanceof HTMLElement) || !submenu.hasAttribute('data-active')) return;

    const headerComponent = submenu.closest('header-component');
    if (!(headerComponent instanceof HTMLElement)) return;

    const submenuHeight = submenu.offsetHeight;
    const isOverlapSituation = headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisibleHeight =
      isOverlapSituation && headerComponent.offsetHeight > 0
        ? headerComponent.querySelector('.header__row--top')?.offsetHeight ?? 0
        : headerComponent.offsetHeight;

    headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
    const fullOpenHeaderHeight = submenuHeight === 0 ? 0 : submenuHeight + headerVisibleHeight;
    headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  let frame = 0;
  function scheduleSync() {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      document.querySelectorAll('.menu-list__submenu[data-active]:has(.cw-mega-menu)').forEach((el) => {
        updateMegaMenuSubmenuMetrics(el);
      });
    });
  }

  function observeMegaSubmenus(root) {
    root.querySelectorAll('.menu-list__submenu:has(.cw-mega-menu)').forEach((submenu) => {
      if (!(submenu instanceof HTMLElement)) return;
      if (submenu.dataset.cwMegaResizeObserved === 'true') return;
      submenu.dataset.cwMegaResizeObserved = 'true';
      new ResizeObserver(scheduleSync).observe(submenu);
    });
  }

  function firstPrimaryIsLink(browse) {
    return (
      browse.querySelector('.cw-mega-menu__primary')?.firstElementChild?.classList.contains(
        'cw-mega-menu__primary-link'
      ) ?? false
    );
  }

  /**
   * @param {HTMLElement} browse
   * @param {string | null} panelIndex - active panel id, or null to clear the column
   */
  function setActivePanel(browse, panelIndex) {
    const activeId = panelIndex ?? '';

    browse.querySelectorAll('[data-cw-mm-trigger]').forEach((trigger) => {
      const isActive = trigger.getAttribute('data-cw-mm-trigger') === activeId;
      trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });

    browse.querySelectorAll('[data-cw-mm-panel]').forEach((panel) => {
      const isActive = panel.getAttribute('data-cw-mm-panel') === activeId;
      panel.classList.toggle('cw-mega-menu__panel--active', isActive);
      panel.toggleAttribute('hidden', !isActive);
    });

    browse.classList.toggle('cw-mega-menu__browse--empty-secondary', !activeId);
    scheduleSync();
  }

  /** @param {HTMLElement} browse */
  function resetBrowse(browse) {
    browse.querySelectorAll('.cw-mega-menu__nested-toggle').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = false;
    });

    const firstTrigger = browse.querySelector('[data-cw-mm-trigger]');
    const defaultPanel =
      firstTrigger && !firstPrimaryIsLink(browse) ? firstTrigger.getAttribute('data-cw-mm-trigger') : null;

    setActivePanel(browse, defaultPanel);
  }

  function observeSubmenuActivation(submenu) {
    if (submenu.dataset.cwMegaActivationObserved === 'true') return;
    submenu.dataset.cwMegaActivationObserved = 'true';

    new MutationObserver((mutations) => {
      const opened = mutations.some(
        (m) => m.type === 'attributes' && m.attributeName === 'data-active' && submenu.hasAttribute('data-active')
      );
      if (!opened) return;

      const browse = submenu.querySelector('[data-cw-mega-browse]');
      if (browse instanceof HTMLElement) resetBrowse(browse);
      scheduleSync();
    }).observe(submenu, { attributes: true, attributeFilter: ['data-active'] });
  }

  function bindMegaBrowse(browse) {
    if (!(browse instanceof HTMLElement) || browse.dataset.cwMegaBrowseBound === 'true') return;
    browse.dataset.cwMegaBrowseBound = 'true';

    browse.querySelectorAll('[data-cw-mm-trigger]').forEach((trigger) => {
      const panelIndex = trigger.getAttribute('data-cw-mm-trigger');
      if (!panelIndex) return;

      const showPanel = () => setActivePanel(browse, panelIndex);
      trigger.addEventListener('mouseenter', showPanel);
      trigger.addEventListener('focus', showPanel);
      trigger.addEventListener('click', showPanel);
    });

    browse.querySelectorAll('.cw-mega-menu__primary-link').forEach((link) => {
      const clearPanel = () => setActivePanel(browse, null);
      link.addEventListener('mouseenter', clearPanel);
      link.addEventListener('focus', clearPanel);
    });

    resetBrowse(browse);

    const submenu = browse.closest('.menu-list__submenu');
    if (submenu instanceof HTMLElement) observeSubmenuActivation(submenu);
  }

  function bindMegaBrowseAll(root) {
    root.querySelectorAll('[data-cw-mega-browse]').forEach((browse) => {
      bindMegaBrowse(/** @type {HTMLElement} */ (browse));
    });
  }

  function init() {
    observeMegaSubmenus(document);
    bindMegaBrowseAll(document);
    scheduleSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('load', scheduleSync, { passive: true });

  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target;
    if (container instanceof HTMLElement) {
      observeMegaSubmenus(container);
      bindMegaBrowseAll(container);
    }
    scheduleSync();
  });

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.type !== 'checkbox' || !target.classList.contains('cw-mega-menu__nested-toggle')) return;
      if (!target.closest('.cw-mega-menu')) return;
      scheduleSync();
    },
    true
  );
})();
